/**
 * NipostPayDashboardPage — landing page for the `/nipost-pay` subtree.
 *
 * Live data (service-financial-services):
 *   - GET  /v1/wallet/wallets-mine                         — citizen's own wallet balance
 *   - GET  /v1/wallet/wallets/:walletId/transactions       — recent activity
 *
 * 404 handling: if the wallet hasn't been created yet the page redirects the
 * citizen to /nipost-pay/onboarding so they can self-provision.
 *
 * Source: `screens/src/pages/nipost-pay/NipostPayDashboard.jsx` (mock-only).
 */
import { type JSX, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownLeft,
  ArrowRightLeft,
  ArrowUpRight,
  CreditCard,
  FileText,
  Loader2,
  Phone,
  Plus,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import {
  listWalletTransactions,
  type WalletTransactionResponse,
} from '../../lib/api/wallet';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import {
  DeferredNotice,
  KycTierBadge,
  PageHeader,
  StatusBadge,
  describeWalletError,
  formatNaira,
  useCustomerWallet,
} from './NipostPayUI';

interface QuickAction {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly path: string;
  readonly color: string;
}

const QUICK_ACTIONS: readonly QuickAction[] = [
  { label: 'Top Up',       icon: Plus,           path: '/nipost-pay/cash-in',       color: 'bg-nipost-green' },
  { label: 'Cash In',      icon: ArrowDownLeft,  path: '/nipost-pay/cash-in',       color: 'bg-blue-600' },
  { label: 'Cash Out',     icon: ArrowUpRight,   path: '/nipost-pay/cash-out',      color: 'bg-orange-500' },
  { label: 'Transfer',     icon: ArrowRightLeft, path: '/nipost-pay/transfer',      color: 'bg-purple-600' },
  { label: 'Bill Payment', icon: FileText,       path: '/nipost-pay/bill-pay',      color: 'bg-teal-600' },
  { label: 'Airtime',      icon: Phone,          path: '/nipost-pay/airtime',       color: 'bg-red-500' },
  { label: 'Debit Card',   icon: CreditCard,     path: '/nipost-pay/card',          color: 'bg-slate-700' },
];

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' });
}

export function NipostPayDashboardPage(): JSX.Element {
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';
  const navigate = useNavigate();
  const wallet = useCustomerWallet(customerId, token);

  // If the wallet query returns 404, the citizen hasn't onboarded yet — send
  // them to the onboarding page automatically.
  useEffect(() => {
    if (wallet.isError) {
      const err = wallet.error as { status?: number; statusCode?: number } | null;
      const status = err?.status ?? err?.statusCode ?? 0;
      if (status === 404) {
        navigate('/nipost-pay/onboarding', { replace: true });
      }
    }
  }, [wallet.isError, wallet.error, navigate]);

  const walletId = wallet.data?.id ?? '';
  const txns = useQuery<{ items: readonly WalletTransactionResponse[] }, Error>({
    queryKey: ['customer-wallet-txns', walletId] as const,
    queryFn: () => listWalletTransactions(walletId, { bearerToken: token ?? undefined, page: 1, pageSize: 8 }),
    enabled: walletId.length > 0 && token !== null,
    retry: false,
    staleTime: 30_000,
  });

  return (
    <div>
      <PageHeader
        title="NIPOST Pay"
        description="Your wallet at a glance — top up, transfer, pay bills, and more."
      />

      {/* Balance + tier card */}
      <div className="bg-gradient-to-br from-nipost-green to-green-800 text-white rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-green-200">Wallet balance</p>
            {wallet.isLoading ? (
              <p className="mt-1 inline-flex items-center gap-2 text-sm text-green-100">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : wallet.data ? (
              <p className="mt-1 text-4xl font-bold">
                {formatNaira(wallet.data.balanceCached ?? wallet.data.balance ?? '0')}
              </p>
            ) : (
              <p className="mt-1 text-2xl font-semibold">—</p>
            )}
            {wallet.data ? (
              <p className="mt-2 text-xs font-mono text-green-200">
                {wallet.data.walletNumber ?? wallet.data.id.slice(0, 12)}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-2">
            <Wallet className="w-8 h-8 text-green-200" />
            {wallet.data ? <KycTierBadge tier={wallet.data.kycTier ?? wallet.data.tier} /> : null}
          </div>
        </div>
      </div>

      {wallet.isError ? (
        <div role="alert" className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 mb-6">
          <p className="font-medium">Could not load wallet</p>
          <p className="mt-1 text-xs">{describeWalletError(wallet.error)}</p>
        </div>
      ) : null}

      {/* Quick actions */}
      <div className="bg-white rounded-xl border border-border p-5 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Quick actions</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.label}
                to={action.path}
                className="flex flex-col items-center gap-2 p-3 rounded-xl border border-border hover:border-nipost-green/40 hover:bg-gray-50 transition-colors"
              >
                <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-xs font-medium text-gray-700 text-center">{action.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-xl border border-border p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Recent activity</h3>
        {txns.isLoading ? (
          <p className="text-sm text-muted inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading transactions…
          </p>
        ) : txns.isError ? (
          <div>
            <DeferredNotice
              heading="Couldn't load your transactions"
              body={txns.error?.message ?? describeWalletError(txns.error)}
            />
            <button
              type="button"
              onClick={() => txns.refetch()}
              disabled={txns.isFetching}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-nipost-green border border-nipost-green/40 rounded-lg hover:bg-nipost-green/5 transition-colors disabled:opacity-60"
            >
              {txns.isFetching ? 'Retrying…' : 'Try again'}
            </button>
          </div>
        ) : txns.data && txns.data.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted text-left border-b border-border">
                <tr>
                  <th className="py-2">Txn</th>
                  <th className="py-2">Action</th>
                  <th className="py-2">Amount</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {txns.data.items.map((t) => (
                  <tr key={t.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-2 font-mono text-xs">{(t.txnNumber ?? t.id).slice(0, 12)}</td>
                    <td className="py-2">{t.action.replace(/_/g, ' ')}</td>
                    <td className="py-2 font-medium">{formatNaira(t.amount)}</td>
                    <td className="py-2">
                      <StatusBadge status={t.status ?? 'COMPLETED'} />
                    </td>
                    <td className="py-2 text-muted">{formatTime(t.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted">No transactions yet — top up to get started.</p>
        )}
      </div>
    </div>
  );
}
